/**
 * Remote dispensing page — dispensing queue for processing and shipping orders.
 *
 * Shows all active orders grouped by patient. Filter cards show patient counts
 * by status. Clicking "View" on a card opens the checkout dialog for that patient.
 */
import { X } from 'lucide-react'
import { parseAsInteger, parseAsString, parseAsStringLiteral, useQueryState } from 'nuqs'
import { useMemo, useState } from 'react'

import { clinicIdSchema } from '@snootsvet/shared'

import { ClinicSearchWithDefault } from '@/frontend/components/clinic-search'
import { Pagination } from '@/frontend/components/pagination'
import { SearchInput } from '@/frontend/components/search-input'
import { Button } from '@/frontend/components/ui/button'
import { Card, CardContent } from '@/frontend/components/ui/card'
import { Dialog, DialogContent } from '@/frontend/components/ui/dialog'
import { Skeleton } from '@/frontend/components/ui/skeleton'
import { CheckoutScreen } from '@/frontend/features/orders/checkout-screen/checkout-screen'
import { useRemoteQueue } from '@/frontend/features/orders/hooks/use-remote-dispensing-queue'
import { groupOrders } from '@/frontend/features/orders/orders.utils'
import { PatientOrderCard } from '@/frontend/features/orders/patient-order-card'
import { PriceEstimateWidget } from '@/frontend/features/orders/price-estimate-widget'
import { useDebounce } from '@/frontend/hooks/use-debounce'
import { useDocumentTitle } from '@/frontend/hooks/use-document-title'
import { cn } from '@/frontend/lib/utils'

// ---------------------------------------------------------------------------
// Filter types
// ---------------------------------------------------------------------------

type ViewFilter = 'pending' | 'ready' | 'out_of_stock'

const viewFilterOptions: { key: ViewFilter; label: string; color: string }[] = [
  { key: 'pending', label: 'Pending', color: 'border-pending-accent bg-pending' },
  { key: 'ready', label: 'Ready', color: 'border-info-accent bg-info' },
  {
    key: 'out_of_stock',
    label: 'Out of Stock',
    color: 'border-muted-foreground bg-muted-foreground/25',
  },
]

const filterKeys = viewFilterOptions.map((o) => o.key) as [ViewFilter, ...ViewFilter[]]

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

/** Remote dispensing page — order processing and shipping queue. */
export function RemoteDispensingPage() {
  useDocumentTitle('Remote Dispensing')
  const [view, setView] = useQueryState(
    'view',
    parseAsStringLiteral(filterKeys).withDefault('pending')
  )
  const [clinicFilter, setClinicFilter] = useQueryState(
    'clinic',
    parseAsStringLiteral(clinicIdSchema.options)
  )
  const [patientSearch, setPatientSearch] = useQueryState('patient', parseAsString)
  const [page, setPage] = useQueryState('page', parseAsInteger.withDefault(1))
  const [checkoutPatientId, setCheckoutPatientId] = useState<string | null>(null)

  const clinicId = clinicFilter ?? undefined

  const debouncedSearch = useDebounce(patientSearch, 300)
  const q = debouncedSearch || undefined

  const queues = {
    pending: useRemoteQueue({ clinicId, q, view: 'pending', page: view === 'pending' ? page : 1 }),
    ready: useRemoteQueue({ clinicId, q, view: 'ready', page: view === 'ready' ? page : 1 }),
    out_of_stock: useRemoteQueue({
      clinicId,
      q,
      view: 'out_of_stock',
      page: view === 'out_of_stock' ? page : 1,
    }),
  }

  const activeQueue = queues[view]

  const groups = useMemo(() => groupOrders(activeQueue.orders ?? []), [activeQueue.orders])

  const showClear = !!patientSearch

  if (activeQueue.error) return <div>Error: {activeQueue.error.message}</div>

  return (
    <div>
      <div className="mb-4">
        <h1 className="text-headline-lg">Remote Dispensing</h1>
        <p className="mt-1 text-muted-foreground">
          Manage remote orders - prepare, charge and dispense.
        </p>
      </div>

      {/* Filter cards + price estimate */}
      <div className="mb-6 flex flex-wrap gap-4">
        <div className="grid min-w-66 max-w-xl flex-1 grid-cols-3 gap-4">
          {viewFilterOptions.map((option) => {
            const count = queues[option.key].totalPatientCount
            return (
              <Card
                key={option.key}
                size="sm"
                className={cn(
                  'w-full cursor-pointer transition-shadow hover:shadow-md',
                  option.color,
                  view === option.key ? 'border-2' : 'border opacity-50'
                )}
                onClick={() => {
                  setView(option.key)
                  setPage(1)
                }}
              >
                <CardContent>
                  <p className="text-sm text-muted-foreground">{option.label}</p>
                  <p className="text-2xl font-bold">{count}</p>
                </CardContent>
              </Card>
            )
          })}
        </div>
        <PriceEstimateWidget />
      </div>

      {/* Filters */}
      <div className="mb-4 flex items-center gap-3">
        <ClinicSearchWithDefault
          includeArchived
          id="filter-clinic"
          value={clinicFilter}
          placeholder="All Clinics"
          onChange={(id) => {
            setClinicFilter(id)
            setPage(1)
          }}
        />
        <SearchInput
          placeholder="Search patient, member, or medication..."
          value={patientSearch ?? ''}
          onChange={(v) => {
            setPatientSearch(v || null)
            setPage(1)
          }}
        />
        {showClear && (
          <Button
            variant="text"
            onClick={() => {
              setPatientSearch(null)
            }}
          >
            <X className="h-3 w-3" />
            Clear filters
          </Button>
        )}
      </div>

      {/* Order list */}
      {!clinicFilter ? (
        <p className="py-8 text-center text-muted-foreground">
          Select a clinic to view the dispensing queue.
        </p>
      ) : activeQueue.isLoading ? (
        <OrderListSkeleton />
      ) : groups.length === 0 ? (
        <p className="py-8 text-center text-muted-foreground">No matching orders.</p>
      ) : (
        <div className="space-y-4">
          {groups.map((group) => (
            <PatientOrderCard key={group.patientId} group={group} onSelect={setCheckoutPatientId} />
          ))}
          <Pagination page={page} totalPages={activeQueue.totalPages} onPageChange={setPage} />
        </div>
      )}

      {/* Checkout dialog */}
      <Dialog open={checkoutPatientId !== null} onOpenChange={() => setCheckoutPatientId(null)}>
        <DialogContent className="sm:max-w-7xl p-4">
          {checkoutPatientId && <CheckoutScreen patientId={checkoutPatientId} />}
        </DialogContent>
      </Dialog>
    </div>
  )
}

// ---------------------------------------------------------------------------
// OrderListSkeleton
// ---------------------------------------------------------------------------

function OrderListSkeleton() {
  return (
    <div className="space-y-4">
      {Array.from({ length: 3 }).map((_, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: skeleton cards have no stable key
        <div key={i} className="rounded-lg border p-4 space-y-3 shadow-xs">
          <div className="flex items-center gap-3">
            <Skeleton className="h-5 w-24" />
            <Skeleton className="h-4 w-32" />
          </div>
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-4 w-1/2" />
        </div>
      ))}
    </div>
  )
}
